import {
  GENERIC_PAYMENT_ICON_URL,
  apmBrandName,
  brandIconUrl,
  getPaymentBrandName,
  isGenericCardLabel,
  normalizeApmBrand,
  normalizeBrand,
  normalizeSupportedWalletBadgeBrand,
} from "./brands";
import type {
  ApmBrand,
  NonCardPaymentMethodDisplayPort,
  PaymentLogoDisplay,
  PaymentMethodDisplayPort,
  ResolvedPaymentMethodDisplay,
  WalletBadgeDisplay,
} from "./ports";

type ApmIdentifierClass = "email" | "handle" | "bank" | "none";

const APM_IDENTIFIER_CLASS: Record<ApmBrand, ApmIdentifierClass> = {
  paypal: "email",
  venmo: "handle",
  upi: "handle",
  ideal: "bank",
  bancontact: "bank",
  eps: "bank",
  klarna: "none",
  affirm: "none",
  afterpay: "none",
  bread: "none",
  skrill: "email",
  blik: "none",
  trustly: "none",
  oxxo: "none",
  pix: "none",
  yape: "none",
  satispay: "none",
  alipay: "none",
  unionpay: "none",
  gcash: "none",
  grabpay: "none",
  maya: "none",
};

const DEFAULT_PAYMENT_ALT = "Payment method";
const MASK_DOT = "•";

export const PAYMENT_TYPE_LABELS: Record<string, string> = {
  card: "Credit Card",
  credit_card: "Credit Card",
  paypal: "PayPal",
  braintree_paypal: "PayPal",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  affirm: "Affirm",
  klarna: "Klarna",
  bread: "Bread",
  iap: "In-App Purchase",
  citcon: "Citcon",
  ppro: "PPRO",
  dlocal: "dLocal",
  droplet: "Droplet",
} as const satisfies Record<string, string>;

export type MaskedCardLabelSegment =
  | { kind: "dots"; count: number }
  | { kind: "gap" }
  | { kind: "text"; value: string };

export function normalizePaymentDescriptor(
  value: string | null | undefined,
): string | null {
  const normalized = value
    ?.replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  return normalized || null;
}

export function isCardPaymentDescriptor(
  value: string | null | undefined,
): boolean {
  const descriptor = normalizePaymentDescriptor(value);
  return descriptor === "card" || descriptor === "credit card";
}

export function paymentDescriptorLabel(
  ...values: Array<string | null | undefined>
): string | null {
  for (const value of values) {
    const normalized = normalizePaymentDescriptor(value);
    if (!normalized) continue;
    const key = normalized.replace(/\s+/g, "_");
    return PAYMENT_TYPE_LABELS[key] ?? value?.trim() ?? null;
  }

  return null;
}

export function maskCardNumber(
  lastFour: string | null | undefined,
  brand?: string | null | undefined,
): string {
  const tail = trimToNull(lastFour) ?? "••••";
  if (normalizeBrand(brand) === "amex") return `•••• •••••• •${tail}`;
  return `•••• •••• •••• ${tail}`;
}

export function cardDisplayLabel(
  lastFour: string | null | undefined,
  brand?: string | null | undefined,
): string {
  const labelBrand = trimToNull(brand);
  const mask = maskCardNumber(lastFour, labelBrand);
  if (
    labelBrand &&
    normalizeBrand(labelBrand) == null &&
    !isGenericCardLabel(labelBrand)
  ) {
    return `${labelBrand} ${mask}`;
  }
  return mask;
}

export function maskedCardLabelSegments(
  label: string | null | undefined,
): MaskedCardLabelSegment[] | null {
  const value = trimToNull(label);
  if (!value) return null;

  const segments: MaskedCardLabelSegment[] = [];
  let hasDots = false;

  const parts = value.split(/\s+/);

  for (const [index, part] of parts.entries()) {
    if (!/^[•\d]+$/.test(part)) return null;
    if (index > 0) segments.push({ kind: "gap" });

    let dotCount = 0;
    while (part[dotCount] === MASK_DOT) dotCount += 1;

    const remainder = part.slice(dotCount);
    if (remainder.includes(MASK_DOT)) return null;

    if (dotCount > 0) {
      hasDots = true;
      segments.push({ kind: "dots", count: dotCount });
    }

    if (remainder) {
      segments.push({ kind: "text", value: remainder });
    }
  }

  return hasDots ? segments : null;
}

export function maskEmail(email: string | null | undefined): string | null {
  const normalizedEmail = trimToNull(email);
  if (!normalizedEmail) return null;
  const at = normalizedEmail.indexOf("@");
  if (at <= 0) return normalizedEmail;
  const local = normalizedEmail.slice(0, at);
  const domain = normalizedEmail.slice(at);
  if (local.length <= 2) return `${local[0] ?? ""}••${domain}`;
  const dots = "•".repeat(Math.min(local.length - 2, 6));
  return `${local[0]}${dots}${local[local.length - 1]}${domain}`;
}

export function apmIdentifier(
  paymentMethod: NonCardPaymentMethodDisplayPort,
): string | null {
  const brand = normalizeApmBrand(
    paymentMethod.brand ?? paymentMethod.fallbackLabel,
  );
  const identifierClass = brand != null ? APM_IDENTIFIER_CLASS[brand] : "none";

  switch (identifierClass) {
    case "email":
      return maskEmail(paymentMethod.emailAddress);
    case "handle":
      return trimToNull(paymentMethod.accountHandle);
    case "bank":
      return bankIdentifier(paymentMethod);
    case "none":
      return null;
  }
}

export function nonCardDisplayLabel(
  paymentMethod: NonCardPaymentMethodDisplayPort,
): string {
  return (
    apmIdentifier(paymentMethod) ??
    apmBrandName(paymentMethod.brand ?? paymentMethod.fallbackLabel) ??
    getPaymentBrandName(paymentMethod.brand ?? paymentMethod.fallbackLabel) ??
    trimToNull(paymentMethod.fallbackLabel) ??
    DEFAULT_PAYMENT_ALT
  );
}

export function resolvePaymentLogo({
  brand,
  logoUrl,
  alt,
}: {
  brand?: string | null;
  logoUrl?: string | null;
  alt?: string | null;
}): PaymentLogoDisplay {
  const canonical = normalizeBrand(brand);
  const apiLogoUrl = trimToNull(logoUrl);
  const fallbackUrl = apiLogoUrl ?? GENERIC_PAYMENT_ICON_URL;

  return {
    primaryUrl: canonical ? brandIconUrl(canonical) : fallbackUrl,
    fallbackUrl: canonical ? fallbackUrl : GENERIC_PAYMENT_ICON_URL,
    alt:
      trimToNull(alt) ??
      getPaymentBrandName(brand) ??
      trimToNull(brand) ??
      DEFAULT_PAYMENT_ALT,
    brand: canonical,
  };
}

export function resolveWalletBadge(
  walletBrand: string | null | undefined,
): WalletBadgeDisplay | null {
  const brand = normalizeSupportedWalletBadgeBrand(walletBrand);
  if (!brand) return null;
  return {
    brand,
    label: getPaymentBrandName(brand) ?? DEFAULT_PAYMENT_ALT,
  };
}

export function resolvePaymentMethodDisplay(
  paymentMethod: PaymentMethodDisplayPort,
): ResolvedPaymentMethodDisplay {
  if (paymentMethod.kind === "card") {
    const label = cardDisplayLabel(paymentMethod.last4, paymentMethod.brand);
    const logo = resolvePaymentLogo({
      brand: paymentMethod.brand,
      logoUrl: paymentMethod.logoUrl,
      alt: getPaymentBrandName(paymentMethod.brand) ?? "Card",
    });
    const walletBadge = resolveWalletBadge(paymentMethod.walletBrand);
    return {
      kind: "card",
      label,
      logo,
      brand: logo.brand,
      walletBadge,
      accessibilityLabel: formatAccessibilityLabel(logo.alt, label),
    };
  }

  const brand = paymentMethod.brand ?? paymentMethod.fallbackLabel;
  const label = nonCardDisplayLabel(paymentMethod);
  const logo = resolvePaymentLogo({
    brand,
    logoUrl: paymentMethod.logoUrl,
    alt: getPaymentBrandName(brand) ?? paymentMethod.fallbackLabel,
  });
  return {
    kind: "non_card",
    label,
    logo,
    brand: logo.brand,
    walletBadge: null,
    accessibilityLabel: formatAccessibilityLabel(logo.alt, label),
  };
}

function bankIdentifier(
  paymentMethod: NonCardPaymentMethodDisplayPort,
): string | null {
  const bankName = trimToNull(paymentMethod.bankName);
  const accountLast4 = trimToNull(paymentMethod.accountLast4);
  const maskedAccount = accountLast4 ? `••${accountLast4}` : null;
  if (bankName && maskedAccount) return `${bankName} ${maskedAccount}`;
  return bankName ?? maskedAccount;
}

function formatAccessibilityLabel(alt: string, label: string): string {
  return alt === label ? label : `${alt} ${label}`;
}

function trimToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
